import React, { useEffect, useMemo, useRef, useState } from 'react';
import config from 'config';
import { isBizzaboInternalWebsiteWhiteLabeled } from '@bizzabo/whitelabel-utils';
import { makeAjaxRequest } from '../ApiUtil';
import FeatureToggleUtils from 'FeatureToggleUtils';
import { UltimateContainer } from '@bizzabo/bap-ultimate';
import { getSessionPageUrl } from './sessionBroadcastUtils';
import ReactionsOverlay from './PlayerWrappers/ReactionsOverlay';
import { sendCustomClickByPosition } from '../../../utils/SnowplowUtils';
import useCaptionHub from './PlayerWrappers/useCaptionHub';

const getEncryptedPayload = async({ groupId, event }) => {
    try {
        const isWl = isBizzaboInternalWebsiteWhiteLabeled(event);
        const path = '/auth/livestream/extractAndTransferTokens?groupId=' + groupId + '&eventId=' + event.id;
        const url = isWl ? `https://${event.whitelabelUrl}${path}` : `${config.eventsUrl}${path}`;
        const response = await makeAjaxRequest(url, { method: 'GET' });
        return response;
    } catch (e) {
        return null;
    }
};

const overrideContainerStyle = {
    position: 'relative',
    background: '#32333C',
    aspectRatio: '16/9',
    width: '100%',
    minHeight: 480,
};
export const STREAMING_MODE = {
    HLS: 'hls',
    HYBRID: 'hybrid',
    WEB_RTC: 'webrtc',
};

export const InteractiveLivestreamFrame = ({
    event,
    session,
    accessResponse,
    accountId,
    parentApplication,
    groupId,
    transportConfig,
    user,
    onStateChanged,
    skipBeforeStreamMessage,
    captionsEnabled = false,
    captionsUrl = null,
}) => {
    const [encryptedPayload, setEncryptedPayload] = useState(null);
    const containerRef = useRef(null);

    const ultimateStreamingMode = FeatureToggleUtils.getFeatureValue('config-virtual-ultimate-streaming-mode');
    const isPollsV2Enabled = FeatureToggleUtils.getFeatureValue('ultimate-polls-2');
    const aggressiveRestartMode = FeatureToggleUtils.getFeatureValue('realtime-notifier-aggressive-mode');
    const enableStreamStatusNotifier = FeatureToggleUtils.isFeatureEnabled('rollout-virtual-enable-curtain-mechanism');
    const sessionBannersEnabled = FeatureToggleUtils.isFeatureEnabled('config-virtual-enable-banners-in-ultimate');
    const captionsProvider = FeatureToggleUtils.getFeatureValue('ultimate-captions-provider');

    const featureToggles = useMemo(() => ({
        ultimateStreamingMode,
        isPollsV2Enabled,
        aggressiveRestartMode,
        enableStreamStatusNotifier,
        sessionBannersEnabled,
        captionsProvider,
    }), [ultimateStreamingMode, isPollsV2Enabled, aggressiveRestartMode, enableStreamStatusNotifier, sessionBannersEnabled, captionsProvider]);

    const plyrVersion = FeatureToggleUtils.getFeatureValue('bap-plyr-io-version');
    const hlsVersion = FeatureToggleUtils.getFeatureValue('bap-hls-js-version');
    const muxVersion = FeatureToggleUtils.getFeatureValue('bap-mux-player-version');
    const bizzaboAgnosticPlayer = FeatureToggleUtils.getFeatureValue('bizzabo-agnostic-player');

    const versionToScriptUrls = useMemo(() => ({
        plyr: plyrVersion,
        hls: hlsVersion,
        mux: muxVersion,
    }), [plyrVersion, hlsVersion, muxVersion]);
    const init = async() => {
        setEncryptedPayload(await getEncryptedPayload({ groupId, event }));
    };

    useEffect(() => {
        if (!encryptedPayload) {
            init();
        }
    }, []);

    const isMuxPlayer = useMemo(() => bizzaboAgnosticPlayer === 'mux', [bizzaboAgnosticPlayer]);

    useCaptionHub({
        enabled: isMuxPlayer && captionsEnabled,
        captionsUrl,
        containerRef,
    });

    const ultimateFunctions = useMemo(() => ({
        onStateChanged,
        getSnowplowHandlers: () => ({
            context: {
                session,
                provider: 'Ultimate',
            },
            sendCustomClickByPosition,
        }),
    }), [onStateChanged, session]);

    return (
        encryptedPayload && (
            <div ref={containerRef} style={{ position: 'relative' }} className="livestream-container">
                <UltimateContainer
                    featureToggles={featureToggles}
                    event={event}
                    session={session}
                    accessResponse={accessResponse}
                    accountId={accountId}
                    groupId={groupId}
                    parentApplication={parentApplication}
                    transportConfig={transportConfig}
                    encryptedPayload={encryptedPayload}
                    overridePlayerConfigs={{ clickToPlay: false }}
                    versionToScriptUrls={versionToScriptUrls}
                    httpFetch={makeAjaxRequest}
                    overrideContainerStyle={overrideContainerStyle}
                    muxUserId={user.id}
                    userEmail={user.primaryEmail}
                    bizzaboAgnosticPlayer={bizzaboAgnosticPlayer}
                    muxEnvKey={config?.muxEnvKey}
                    getSessionPageUrl={getSessionPageUrl}
                    functions={ultimateFunctions}
                    skipBeforeStreamMessage={skipBeforeStreamMessage}
                    showReactions={false}
                />
                <ReactionsOverlay activity={session} />
            </div>
        )
    );
};
